import './Proforma.scss';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { downloadFile, formatDate } from '../../../core/helpers';
import { ReactComponent as ArrowDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as DownloadIcon } from '../../../icons/download.svg';
import { useTaxAdvisor } from '../tax-advisor-context';
import Tabs from '../../../components/Tabs/Tabs';

interface ProformaFile {
  name: string;
  href: string;
}

interface ProformaItem {
  date: Date;
  items: { country: string; files: ProformaFile[] }[];
}

interface Props {
  page?: boolean;
}

const TABS = [2022, 2021];

export default function Proforma({ page = true }: Props) {
  const { company } = useTaxAdvisor();
  const { t } = useTranslation();

  const renderContent = (activeTab: number) => (
    <>
      <div>
        {getData(activeTab).map((d, i, self) => (
          <div key={d.date.getMonth()} className='Proforma__item'>
            <div className='fw-800 mb-2 Text--20'>
              {formatDate(d.date, 'MMMM')}
            </div>
            {d.items.map((item) => (
              <div
                className='d-flex justify-content-between align-items-center border-top py-2'
                key={item.country}
              >
                <div>{t(item.country)}</div>
                <div>
                  <Dropdown
                    horizontalAlignment='left'
                    className='Dropdown--Proforma'
                    trigger={
                      <div className='d-flex align-items-center'>
                        <div className='Proforma__triggerLabel d-flex align-items-center '>
                          <DownloadIcon className='SellerManualUploadInstructionBlock__download me-2' />
                          <div className='text-truncate text-start flex-grow-1 pe-1 fw-600'>
                            {t('Download')}
                          </div>
                        </div>
                        <div className='Proforma__arrow d-flex flex-shrink-0'>
                          <ArrowDownIcon className='m-auto' />
                        </div>
                      </div>
                    }
                    content={
                      <>
                        {item.files.map((f) => {
                          return (
                            <button
                              className='d-block Proforma__menuItem w-100 text-start'
                              key={f.name}
                              onClick={() => {
                                const parts = f.href.toLowerCase().split('.');

                                downloadFile(
                                  f.href,
                                  parts[parts.length - 1] === 'pdf'
                                    ? `PRO-${
                                        company?.name
                                          ? `${company.name
                                              .split(' ')
                                              .join('_')}-`
                                          : ''
                                      }${formatDate(d.date, 'yyyy-MM')}-${
                                        item.country
                                      }-${f.name.split('.')[0]}`
                                    : `PRO-${
                                        company?.name
                                          ? `${company.name
                                              .split(' ')
                                              .join('_')}-`
                                          : ''
                                      }${formatDate(d.date, 'yyyy-MM')}-${
                                        item.country
                                      }`
                                );
                              }}
                            >
                              {f.name}
                            </button>
                          );
                        })}
                      </>
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );

  if (page) {
    return (
      <div className='Page flex-grow-1'>
        <div className='container'>
          <div className='Paper p-4 Paper--sm mx-auto'>
            <div className='Text--30 fw-900 mb-4'>{t('Pro-Forma')}</div>
            <Tabs tabs={TABS} renderContent={renderContent} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='mb-1'>{TABS[0]}</div>
      {renderContent(TABS[0])}
    </>
  );
}

const ITEM = {
  items: [
    {
      country: 'DE',
      files: [
        {
          name: 'ZIP with PDFs',
          href: '/proforma/PRO-abc-gmbh-2022-08-DE-99.zip',
        },
        {
          name: 'PL.pdf',
          href: '/proforma/PRO-abc-gmbh-2022-08-DE-PL-99.pdf',
        },
        {
          name: 'CZ.pdf',
          href: '/proforma/PRO-abc-gmbh-2022-08-DE-CZ-99.pdf',
        },
        {
          name: 'XLSX file',
          href: '/proforma/PRO-0-abc-gmbh-2022-08-DE-99.xlsx',
        },
      ],
    },
    {
      country: 'FR',
      files: [
        {
          name: 'ZIP with PDFs',
          href: '/proforma/PRO-abc-gmbh-2022-08-FR-99.zip',
        },
        {
          name: 'DE.pdf',
          href: '/proforma/PRO-abc-gmbh-2022-08-FR-DE-99.pdf',
        },
        {
          name: 'XLSX file',
          href: '/proforma/PRO-0-abc-gmbh-2022-08-FR-99.xlsx',
        },
      ],
    },
    {
      country: 'CZ',
      files: [
        {
          name: 'ZIP with PDFs',
          href: '/proforma/PRO-abc-gmbh-2022-08-CZ-99.zip',
        },
        {
          name: 'DE.pdf',
          href: '/proforma/PRO-abc-gmbh-2022-08-CZ-DE-99.pdf',
        },
        {
          name: 'PL.pdf',
          href: '/proforma/PRO-abc-gmbh-2022-08-CZ-PL-99.pdf',
        },
        {
          name: 'XLSX file',
          href: '/proforma/PRO-0-abc-gmbh-2022-08-CZ-99.xlsx',
        },
      ],
    },
  ],
};

const getData: (v: number) => ProformaItem[] = (year: number) => [
  ...(year === 2021
    ? Array(3)
        .fill(ITEM)
        .map((item, i) => ({ ...item, date: new Date(year, 11 - i, 1) }))
    : []),
  ...Array(9)
    .fill(ITEM)
    .map((item, i) => ({ ...item, date: new Date(year, 8 - i, 1) })),
];
